import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Construction, MapPin, Play } from "lucide-react";
import SEO from "../components/SEO";
import { usePageContent } from "../lib/usePageContent";
import { resolveImage } from "../lib/data";

const DEFAULTS = {
  eyebrow: "Cantiere · Aggiornamento Giugno 2026",
  title_main: "Gateway Hotel",
  title_accent: "Rho Fiera.",
  hero_intro:
    "Otto piani di cemento armato emergono accanto a MiCo Milano Congressi. L'edificio prende forma — superato il 50% di completamento strutturale.",
  location: "Via Lampedusa, Rho · Adiacente MiCo",
  opening_label: "Apertura Q2 2027",
  kpi_1_label: "Piani fuori terra",
  kpi_1_value: "8",
  kpi_2_label: "Camere a regime",
  kpi_2_value: "180",
  kpi_3_label: "Completamento strutturale",
  kpi_3_value: "50%",
  kpi_4_label: "Mesi alla consegna",
  kpi_4_value: "~10",
  project_eyebrow: "Il progetto",
  project_title: "Un nuovo hub per il distretto fieristico più visitato d'Europa.",
  project_paragraph_1: "",
  project_paragraph_2: "",
  gallery_eyebrow: "Dal cantiere",
  gallery_title: "Lo stato dei lavori.",
  hero_image: "/brand/cantiere/gateway/gateway-03.jpg",
  video_url: "/brand/cantiere/gateway/gateway-video.mp4",
  video_poster: "/brand/cantiere/gateway/gateway-02.jpg",
  gallery: [],
  cta_eyebrow: "Riservato investitori",
  cta_title: "Vuoi gli aggiornamenti completi del cantiere?",
  cta_description: "",
  cta_button_label: "Accedi",
};

export default function CantiereGateway() {
  const c = usePageContent("cantiere-gateway", DEFAULTS);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const gallery = Array.isArray(c.gallery) && c.gallery.length ? c.gallery : DEFAULTS.gallery;

  return (
    <>
      <SEO
        title={`${c.title_main || "Cantiere"} · ${c.title_accent || ""}`.trim()}
        description={c.hero_intro || ""}
      />

      {/* ============================ HERO ============================ */}
      <section
        ref={heroRef}
        data-testid="cantiere-hero"
        className="relative min-h-[100svh] overflow-hidden bg-black"
      >
        <motion.div
          style={{ scale: heroScale, opacity: heroOpacity }}
          className="absolute inset-0"
        >
          <img
            src={resolveImage(c.hero_image) || DEFAULTS.hero_image}
            alt={c.title_main || "Cantiere"}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        </motion.div>
        {/* Gradient overlay (stronger on mobile to keep text readable) */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/40 md:via-black/40 md:to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />

        {/* Top nav strip */}
        <div className="absolute top-0 inset-x-0 z-10 px-6 md:px-12 pt-24 md:pt-32">
          <Link
            to="/nuove-aperture"
            data-testid="cantiere-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-mono text-white/70 hover:text-[#C48F56] transition-colors"
          >
            <ArrowLeft size={14} /> Torna alle nuove aperture
          </Link>
        </div>

        {/* Hero text */}
        <div className="absolute inset-0 z-10 flex items-end px-6 md:px-12 pb-16 md:pb-24">
          <div className="max-w-4xl w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex items-center gap-2 text-[10px] md:text-[11px] uppercase tracking-[0.25em] md:tracking-[0.3em] font-mono text-[#C48F56]"
            >
              <Construction size={13} className="shrink-0" />
              <span className="leading-snug">{c.eyebrow}</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 md:mt-6 font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] tracking-tightest leading-[1.02]"
            >
              {c.title_main}
              <br />
              <span className="text-[#C48F56]">{c.title_accent}</span>
            </motion.h1>

            {c.hero_intro && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="mt-5 md:mt-6 text-white/75 text-base md:text-xl max-w-2xl leading-relaxed"
              >
                {c.hero_intro}
              </motion.p>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-8 md:mt-10 flex items-start md:items-center gap-3 md:gap-4 text-[10px] md:text-[11px] uppercase tracking-[0.2em] md:tracking-[0.25em] font-mono text-white/55 flex-wrap"
            >
              <MapPin size={13} className="text-[#C48F56] shrink-0 mt-0.5 md:mt-0" />
              <span>{c.location}</span>
              <span className="opacity-30 hidden md:inline">·</span>
              <span>{c.opening_label}</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================ KPI BAR ============================ */}
      <section className="relative bg-[#0A0A0D] border-y border-white/5 py-8 md:py-10 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          <KpiBlock label={c.kpi_1_label} value={c.kpi_1_value} />
          <KpiBlock label={c.kpi_2_label} value={c.kpi_2_value} />
          <KpiBlock label={c.kpi_3_label} value={c.kpi_3_value} accent />
          <KpiBlock label={c.kpi_4_label} value={c.kpi_4_value} />
        </div>
      </section>

      {/* ============================ VIDEO + INTRO ============================ */}
      <section className="relative py-20 md:py-32 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="flex items-center gap-3 text-[10px] md:text-[11px] uppercase tracking-[0.25em] md:tracking-[0.3em] text-white/50">
              <span className="h-px w-6 md:w-8 bg-[#C48F56]" />
              {c.project_eyebrow}
            </div>
            <h2 className="mt-4 md:mt-5 font-display text-3xl sm:text-4xl md:text-5xl tracking-tightest leading-[1.05]">
              {c.project_title}
            </h2>
            {c.project_paragraph_1 && (
              <p className="mt-5 md:mt-6 text-white/65 leading-relaxed text-sm md:text-base">
                {c.project_paragraph_1}
              </p>
            )}
            {c.project_paragraph_2 && (
              <p className="mt-3 md:mt-4 text-white/65 leading-relaxed text-sm md:text-base">
                {c.project_paragraph_2}
              </p>
            )}
            <div className="mt-7 md:mt-8 flex flex-wrap gap-3">
              <Link
                to="/investor"
                data-testid="cantiere-investor-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-medium hover:bg-[#C48F56] transition-colors"
              >
                Area investitori <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/contatti"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm hover:bg-white/5"
              >
                Contattaci
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <VideoBlock videoUrl={c.video_url} poster={c.video_poster} />
          </div>
        </div>
      </section>

      {/* ============================ GALLERY ============================ */}
      <section className="relative pb-20 md:pb-32 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="flex items-end justify-between mb-10 md:mb-14 gap-4">
          <div>
            <div className="flex items-center gap-3 text-[10px] md:text-[11px] uppercase tracking-[0.25em] md:tracking-[0.3em] text-white/50">
              <span className="h-px w-6 md:w-8 bg-[#C48F56]" />
              {c.gallery_eyebrow}
            </div>
            <h2 className="mt-4 md:mt-5 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tightest">
              {c.gallery_title}
            </h2>
          </div>
          {gallery.length > 0 && (
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] md:tracking-[0.25em] text-white/40 whitespace-nowrap">
              {String(gallery.length).padStart(2, "0")} scatti
            </span>
          )}
        </div>

        {/* Mobile: 1-column stack with proper aspect ratios.
            Desktop: asymmetric 12-col grid. */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-5">
          {gallery.map((img, i) => (
            <GalleryShot
              key={i}
              src={img.image}
              caption={img.caption}
              priority={i === 0}
              className={shotClassByIndex(i, gallery.length)}
            />
          ))}
        </div>
      </section>

      {/* ============================ INVESTOR CTA STRIP ============================ */}
      <section className="relative pb-16 md:pb-20 px-6 md:px-12">
        <div className="max-w-[1440px] mx-auto rounded-2xl border border-[#C48F56]/20 bg-gradient-to-br from-[#C48F56]/8 via-transparent to-transparent p-8 md:p-16">
          <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center">
            <div className="md:col-span-8">
              <div className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] md:tracking-[0.3em] text-[#C48F56]">
                {c.cta_eyebrow}
              </div>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl md:text-5xl tracking-tightest leading-[1.05]">
                {c.cta_title}
              </h3>
              {c.cta_description && (
                <p className="mt-4 md:mt-5 text-white/65 leading-relaxed max-w-2xl text-sm md:text-base">
                  {c.cta_description}
                </p>
              )}
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/investor"
                data-testid="cantiere-investor-cta-strip"
                className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-[#C48F56] transition-colors"
              >
                {c.cta_button_label} <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Asymmetric layout helper for gallery shots.
 * Pattern: shot 1 = hero, shots 2-3 verticali, shot 4+ wide
 *
 * Mobile (1 col): all full-width with sensible 16/10 aspect.
 */
function shotClassByIndex(i, total) {
  // Mobile is always col-span-1 + aspect-[16/10]
  const mobile = "col-span-1 aspect-[16/10]";
  // Desktop: asymmetric pattern
  if (i === 0) return `${mobile} md:col-span-8 md:row-span-2 md:aspect-[16/11]`;
  if (i === 1) return `${mobile} md:col-span-4 md:aspect-[4/5]`;
  if (i === 2) return `${mobile} md:col-span-4 md:aspect-[4/5]`;
  if (i === 3) return `${mobile} md:col-span-12 md:aspect-[21/9]`;
  // Fallback for additional shots
  return `${mobile} md:col-span-6 md:aspect-[16/10]`;
}

function KpiBlock({ label, value, accent = false }) {
  return (
    <div>
      <div className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] md:tracking-[0.25em] text-white/40 leading-snug">
        {label}
      </div>
      <div
        className={`mt-2 font-display text-3xl md:text-5xl tracking-tight leading-none ${
          accent ? "text-[#C48F56]" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function VideoBlock({ videoUrl, poster }) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const url = resolveImage(videoUrl) || videoUrl;
  const posterUrl = resolveImage(poster) || poster;

  const start = () => {
    setPlaying(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-video bg-[#0F0F14]">
      <video
        ref={videoRef}
        src={url}
        poster={posterUrl}
        playsInline
        loop
        muted
        controls={playing}
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {!playing && (
        <button
          type="button"
          onClick={start}
          data-testid="cantiere-video-play"
          aria-label="Riproduci video cantiere"
          className="absolute inset-0 grid place-items-center bg-gradient-to-t from-black/60 to-black/10 hover:from-black/70 transition-colors group"
        >
          <span className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-white/90 text-black grid place-items-center group-hover:bg-[#C48F56] group-hover:scale-110 transition-all">
            <Play size={22} className="ml-1 md:size-7" fill="currentColor" />
          </span>
          <span className="absolute bottom-4 md:bottom-6 left-4 md:left-6 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.25em] md:tracking-[0.3em] text-white/80">
            Video cantiere
          </span>
        </button>
      )}
    </div>
  );
}

function GalleryShot({ src, caption, className, priority = false }) {
  const resolved = resolveImage(src) || src;
  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`relative overflow-hidden rounded-xl md:rounded-2xl border border-white/10 group ${className}`}
    >
      <img
        src={resolved}
        alt={caption || ""}
        loading={priority ? "eager" : "lazy"}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-105"
      />
      {/* Mobile: caption sempre visibile in basso (no hover su touch) */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500" />
      {caption && (
        <figcaption className="absolute bottom-0 inset-x-0 p-3 md:p-6 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.2em] md:tracking-[0.25em] text-white/85 md:text-white/80 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}
